import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { usePlaylist } from '../utils/playlist';
import '../styles/listen.css';

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmt(t) {
  if (!isFinite(t) || t < 0) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

const IconPlay = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);
const IconPause = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);
const IconSkipNext = ({ flip = false }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true" style={flip ? { transform: 'scaleX(-1)' } : undefined}>
    <path d="M6 5l8 7-8 7V5zm10 0h2v14h-2z" />
  </svg>
);
const IconStepNext = ({ flip = false }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={flip ? { transform: 'scaleX(-1)' } : undefined}>
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

export default function Listen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const playlist = usePlaylist();
  const [searchParams] = useSearchParams();
  const initialLang = searchParams.get('lang') === 'ar' ? 'ar' : 'en';

  const [book, setBook] = useState(null);
  const [allBooks, setAllBooks] = useState([]);
  const [error, setError] = useState('');
  const [lang, setLang] = useState(initialLang);
  const [pageIdx, setPageIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioRef = useRef(null);
  const textRef = useRef(null);
  const timingsRef = useRef([]);
  const activeIdxRef = useRef(-1);
  const rafRef = useRef(0);

  // Fetch the requested book + the catalog (so we can resolve playlist queue items)
  useEffect(() => {
    let alive = true;
    // Reset per-book transient state so switching tracks doesn't leave us on
    // the previous book's page index / progress bar.
    // Also clear `book` immediately — otherwise the old book briefly remains
    // mounted while the new fetch is in flight, and `currentAudioUrl` becomes
    // old-book/page-0 for one render. The audio effect then runs load()+play()
    // for that stale URL, and the moment the new book arrives a second load()
    // aborts the pending play promise with AbortError → catch handler kills
    // playback and the queue stalls. Auto-advance after end-of-book breaks.
    setBook(null);
    setPageIdx(0);
    setTime(0);
    setDuration(0);
    (async () => {
      try {
        const { data: b } = await axios.get(`/api/books/${id}`, { headers: authHeaders(), timeout: 10000 });
        if (alive) setBook(b);
      } catch (err) {
        if (alive) setError(err?.response?.data?.message || 'Could not load this book.');
      }
    })();
    axios.get('/api/books', { timeout: 4000 }).then(({ data }) => alive && setAllBooks(Array.isArray(data) ? data : [])).catch(() => {});
    return () => { alive = false; };
  }, [id]);

  // Sorted, non-cover pages of the current book
  const pages = useMemo(() => {
    const all = book?.pages || [];
    return all.slice().sort((a, b) => a.pageNumber - b.pageNumber);
  }, [book]);

  const queue = useMemo(() => {
    const ids = Array.from(playlist.all());
    const byId = new Map();
    allBooks.forEach((b) => byId.set(String(b._id), b));
    if (book && !byId.has(String(book._id))) byId.set(String(book._id), book);
    return ids.map((bid) => byId.get(String(bid))).filter(Boolean);
  }, [playlist, allBooks, book]);

  const currentPage = pages[pageIdx];
  const currentAudioUrl = currentPage ? (lang === 'ar' ? currentPage.audioAr : currentPage.audioEn) : '';
  const text = currentPage ? (lang === 'ar' ? currentPage.textAr : currentPage.textEn) : '';
  const isRtl = lang === 'ar';

  // Reload audio + timings whenever the source changes
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !currentAudioUrl) return;
    a.src = currentAudioUrl;
    a.load();
    setTime(0);
    // Reset highlight state and fetch sidecar timings (same path, .mp3 → .json)
    timingsRef.current = [];
    activeIdxRef.current = -1;
    if (textRef.current) {
      textRef.current.querySelectorAll('.tts-word.word-active').forEach((el) => el.classList.remove('word-active'));
    }
    let cancelled = false;
    fetch(currentAudioUrl.replace(/\.mp3$/, '.json'))
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (!cancelled) timingsRef.current = Array.isArray(data) ? data : []; })
      .catch(() => { if (!cancelled) timingsRef.current = []; });
    if (playing) {
      a.play().catch((err) => {
        // AbortError fires when a fresh load() interrupts a pending play() —
        // happens normally when we switch tracks. Treat as transient: don't
        // toggle playing off (which would stop the queue auto-advance).
        if (err && err.name === 'AbortError') return;
        setPlaying(false);
      });
    }
    return () => { cancelled = true; };
  }, [currentAudioUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync playing state to <audio>
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.play().catch((err) => {
        if (err && err.name === 'AbortError') return;
        setPlaying(false);
      });
    } else {
      a.pause();
    }
  }, [playing]);

  // Word highlighter — runs while playing, drives DOM imperatively to avoid re-render
  useEffect(() => {
    if (!playing) {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
      return;
    }
    const tick = () => {
      const a = audioRef.current;
      const target = textRef.current;
      const timings = timingsRef.current;
      if (!a || !target || !timings.length) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const t = a.currentTime;
      let next = -1;
      for (let i = 0; i < timings.length; i++) {
        if (t >= timings[i].offset && t < timings[i].offset + timings[i].duration + 0.15) {
          next = i; break;
        }
      }
      if (next !== activeIdxRef.current) {
        if (activeIdxRef.current >= 0) {
          const prev = target.querySelector(`.tts-word[data-idx="${activeIdxRef.current}"]`);
          if (prev) prev.classList.remove('word-active');
        }
        if (next >= 0) {
          const span = target.querySelector(`.tts-word[data-idx="${next}"]`);
          if (span) {
            span.classList.add('word-active');
            // Auto-scroll within the text panel if needed
            const r = span.getBoundingClientRect();
            const cr = target.getBoundingClientRect();
            if (r.bottom > cr.bottom - 8 || r.top < cr.top + 8) {
              span.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            }
          }
        }
        activeIdxRef.current = next;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    };
  }, [playing]);

  const handleEnded = useCallback(() => {
    if (pageIdx < pages.length - 1) {
      setPageIdx((i) => i + 1);
      setPlaying(true);
    } else {
      // Move to next book in queue if present. Re-assert playing=true
      // before navigation so the next book auto-plays even if a stray
      // AbortError set it to false during the previous track swap.
      const idx = queue.findIndex((b) => String(b._id) === String(book?._id));
      if (idx >= 0 && idx < queue.length - 1) {
        setPlaying(true);
        navigate(`/listen/${queue[idx + 1]._id}?lang=${lang}`);
      } else {
        setPlaying(false);
      }
    }
  }, [pageIdx, pages.length, queue, book, navigate, lang]);

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a) return;
    setTime(a.currentTime);
    if (a.duration && !isNaN(a.duration)) setDuration(a.duration);
  };

  const onLoadedMeta = () => {
    const a = audioRef.current;
    if (a && a.duration && !isNaN(a.duration)) setDuration(a.duration);
  };

  const togglePlay = () => setPlaying((p) => !p);
  const stepPage = (delta) => {
    const next = Math.max(0, Math.min(pages.length - 1, pageIdx + delta));
    if (next !== pageIdx) setPageIdx(next);
  };
  const skipBookNext = () => {
    const idx = queue.findIndex((b) => String(b._id) === String(book?._id));
    if (idx >= 0 && idx < queue.length - 1) navigate(`/listen/${queue[idx + 1]._id}?lang=${lang}`);
  };
  const skipBookPrev = () => {
    const idx = queue.findIndex((b) => String(b._id) === String(book?._id));
    if (idx > 0) navigate(`/listen/${queue[idx - 1]._id}?lang=${lang}`);
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const ratio = parseFloat(e.target.value);
    a.currentTime = ratio * duration;
    setTime(a.currentTime);
  };

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.target.matches('input, textarea')) return;
      if (e.key === ' ') { e.preventDefault(); togglePlay(); }
      else if (e.key === 'ArrowRight') stepPage(1);
      else if (e.key === 'ArrowLeft')  stepPage(-1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  if (error) {
    return (
      <div className="listen-wrapper">
        <div className="listen-orb listen-orb-1" aria-hidden="true" />
        <div className="listen-orb listen-orb-2" aria-hidden="true" />
        <div className="listen-shell">
          <div className="listen-error">
            <h2>Couldn’t load this book.</h2>
            <p>{error}</p>
            <Link to="/profile#playlist" className="listen-btn">Back to playlist</Link>
          </div>
        </div>
      </div>
    );
  }
  if (!book) {
    return (
      <div className="listen-wrapper">
        <div className="listen-orb listen-orb-1" aria-hidden="true" />
        <div className="listen-orb listen-orb-2" aria-hidden="true" />
        <div className="listen-shell">
          <div className="listen-loading">
            <div className="listen-spinner" />
            <p>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  const cover = (lang === 'ar' && book.coverImageAr) ? book.coverImageAr : book.coverImage;
  const title = (lang === 'ar' && book.titleAr) ? book.titleAr : book.title;
  const progressRatio = duration > 0 ? time / duration : 0;

  return (
    <div className="listen-wrapper" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="listen-orb listen-orb-1" aria-hidden="true" />
      <div className="listen-orb listen-orb-2" aria-hidden="true" />
      <div className="listen-shell">

      <header className="listen-head">
        <button
          type="button"
          className="listen-back"
          aria-label={isRtl ? 'رجوع' : 'Back'}
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/profile#playlist'))}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={isRtl ? '10 18 16 12 10 6' : '14 6 8 12 14 18'} />
          </svg>
          <span>{isRtl ? 'رجوع' : 'Back'}</span>
        </button>
        <div className="listen-lang">
          <button type="button" className={`listen-lang-btn ${lang === 'en' ? 'is-active' : ''}`} onClick={() => setLang('en')}>EN</button>
          <button type="button" className={`listen-lang-btn ${lang === 'ar' ? 'is-active' : ''}`} onClick={() => setLang('ar')}>ع</button>
        </div>
      </header>

      <div className="listen-stage">
        <div className="listen-cover">
          <img src={cover} alt={title} draggable={false} />
        </div>

        <h1 className="listen-title">{title}</h1>
        <div className="listen-author">{book.author}</div>

        <div className="listen-meta-row">
          <div className="listen-page-meta">
            {isRtl
              ? `صفحة ${currentPage?.pageNumber ?? 0} من ${(pages[pages.length - 1]?.pageNumber) ?? pages.length - 1}`
              : `Page ${currentPage?.pageNumber ?? 0} of ${(pages[pages.length - 1]?.pageNumber) ?? pages.length - 1}`}
          </div>
          <div className="listen-lang listen-lang-stage" role="group" aria-label="Language">
            <button type="button" className={`listen-lang-btn ${lang === 'en' ? 'is-active' : ''}`} onClick={() => setLang('en')}>EN</button>
            <button type="button" className={`listen-lang-btn ${lang === 'ar' ? 'is-active' : ''}`} onClick={() => setLang('ar')}>ع</button>
          </div>
        </div>

        {text && (() => {
          const parts = text.split(/(\s+)/);
          let wIdx = 0;
          return (
            <div className="listen-text" key={`${pageIdx}-${lang}`}>
              <p ref={textRef}>
                {parts.map((part, i) => {
                  if (/^\s+$/.test(part)) {
                    return part.includes('\n')
                      ? <br key={`br-${i}`} />
                      : <span key={`sp-${i}`}>{part}</span>;
                  }
                  const idx = wIdx++;
                  return (
                    <span key={`w-${i}`} className="tts-word" data-idx={idx}>
                      {part}
                    </span>
                  );
                })}
              </p>
            </div>
          );
        })()}

        <div className="listen-progress">
          <span className="listen-time">{fmt(time)}</span>
          <input
            className="listen-seek"
            type="range"
            min={0}
            max={1}
            step={0.001}
            value={progressRatio}
            onChange={onSeek}
            aria-label="Seek"
            style={{ '--p': `${Math.round(progressRatio * 100)}%` }}
          />
          <span className="listen-time">{fmt(duration)}</span>
        </div>

        <div className="listen-controls">
          <button type="button" className="listen-ctl" onClick={skipBookPrev} aria-label="Previous book" disabled={queue.findIndex((b) => String(b._id) === String(book._id)) <= 0}>
            <IconSkipNext flip={!isRtl} />
          </button>
          <button type="button" className="listen-ctl listen-ctl-step" onClick={() => stepPage(-1)} aria-label="Previous page" disabled={pageIdx <= 0}>
            <IconStepNext flip={!isRtl} />
          </button>
          <button type="button" className="listen-ctl listen-ctl-play" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? <IconPause /> : <IconPlay />}
          </button>
          <button type="button" className="listen-ctl listen-ctl-step" onClick={() => stepPage(1)} aria-label="Next page" disabled={pageIdx >= pages.length - 1}>
            <IconStepNext flip={isRtl} />
          </button>
          <button type="button" className="listen-ctl" onClick={skipBookNext} aria-label="Next book" disabled={queue.findIndex((b) => String(b._id) === String(book._id)) === queue.length - 1 || queue.length === 0}>
            <IconSkipNext flip={isRtl} />
          </button>
        </div>

        <audio
          ref={audioRef}
          preload="metadata"
          onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={onLoadedMeta}
          onEnded={handleEnded}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />
      </div>

      {queue.length > 0 && (
        <aside className="listen-queue">
          <h2 className="listen-queue-title">{isRtl ? 'قائمتك' : 'Up next in your playlist'}</h2>
          <ul>
            {queue.map((b) => {
              const isActive = String(b._id) === String(book._id);
              return (
                <li key={b._id} className={`listen-queue-item ${isActive ? 'is-active' : ''}`}>
                  <Link to={`/listen/${b._id}?lang=${lang}`} className="listen-queue-link">
                    <div className="listen-queue-cover">
                      <img src={(lang === 'ar' && b.coverImageAr) ? b.coverImageAr : b.coverImage} alt={b.title} loading="lazy" />
                      {isActive && (
                        <span className="listen-queue-now" aria-hidden="true">
                          <span /><span /><span /><span />
                        </span>
                      )}
                    </div>
                    <div className="listen-queue-meta">
                      <div className="listen-queue-name">{(lang === 'ar' && b.titleAr) ? b.titleAr : b.title}</div>
                      <div className="listen-queue-author">{b.author}</div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </aside>
      )}
      </div>
    </div>
  );
}
